import { PrimaryButton, Stack, TextField, Link } from "@fluentui/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Contract } from "../contract.interface";
import { Container } from "../PaymentMethodStep/styles";

import { DisplayTermDataDraft } from "./components/DisplayAccountData";
import EditComplementaryDocuments from "./components/EditComplementaryDocumentsForm";
import { OwnershipTransferShimmer } from "./components/OwnershipTransferShimmer";
import { ownershipTransferFieldsSchema } from "./fields/ownership-transfer-fields-schema";
import { SubmitOwnershipTransferFieldsValues } from "./fields/ownership-transfer-fields.interface";
import { ownershipTransferFields } from "./fields/OwnershipTransferFields";
import { submitTermDataDraft } from "./utils/submitTermDataDraft";

import { Card } from "@/components/Shared/Card";
import { useGetTermDataDraft } from "@/core/libs/api/react-query/hook/use-get-term-data-draft";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { useDebounce } from "@/hooks/useDebounce";
import { AccountDocumentType, AccountType } from "@/modules/Accounts/types";
import {
  ITermDataDraft,
  TermTypeEnum
} from "@/modules/Terms/interfaces/ITerm.interface";
import { cpfCnpjFormatter } from "@/utils/FormatCPForCNPJ";
import { normalizeDocumentNumber } from "@/utils/NormalizeDocumentNumber";
import { ValidateCNPJ } from "@/utils/ValidateCNPJ";
import { ValidateCPF } from "@/utils/ValidateCPF";
import { validateDocumentNumber } from "@/utils/ValidateDocumentNumber";

interface OwnershipTransferStepProps {
  nextStep?: (contract: Contract) => void;
  contract: Contract;
}

export function OwnershipTransferStep(
  props: OwnershipTransferStepProps
): JSX.Element {
  const { nextStep, contract } = props;
  const didInit = useRef(false);

  const [canAdvance, setCanAdvance] = useState<boolean>(false);
  const [assignorAccountType, setAssignorAccountType] = useState<AccountType>(
    AccountType.PHYSICAL
  );
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm: string = useDebounce(searchTerm, 500);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [useMemberDataText, setUseMemberDataText] = useState<string>("");
  const [formInitialValues, setFormInitialValues] = useState(undefined);
  const [isUsingAccountDocument, setIsUsingAccountDocument] =
    useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { execute } = useApi();

  const termDataDraft = useGetTermDataDraft(contract.id);
  const termDataDraftList = termDataDraft.list;

  const handleNextStep = useCallback(() => {
    nextStep(contract);
  }, [contract, nextStep]);

  const values = Array.isArray(termDataDraft?.list)
    ? termDataDraftList[0]
    : undefined;

  useEffect(() => {
    if (didInit.current || termDataDraft.isLoadingList) return;

    didInit.current = true;

    const accountPrimaryDocument = contract?.account?.documents?.find(
      doc =>
        doc.documentType === AccountDocumentType.CPF ||
        doc.documentType === AccountDocumentType.CNPJ
    );

    setUseMemberDataText(
      `Usar ${cpfCnpjFormatter(accountPrimaryDocument.documentNumber)} de ${contract?.account?.fullName}`
    );

    const currentTermDataDraft = Array.isArray(termDataDraftList)
      ? termDataDraftList.find(
          term => term.termType === TermTypeEnum.CHANGE_OF_OWNERSHIP
        )
      : undefined;

    if (currentTermDataDraft) {
      setSearchTerm(currentTermDataDraft.documentNumber);
      setCanAdvance(true);
    } else {
      setCanAdvance(false);
    }
  }, [
    contract?.account.accountType,
    contract?.account.documents,
    contract?.account.fullName,
    termDataDraft.isLoadingList,
    termDataDraftList
  ]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      const documentNumber = normalizeDocumentNumber(debouncedSearchTerm);
      const documentNumberIsAccountPrimaryDocument =
        contract?.account?.documents?.some(
          doc => doc.documentNumber === documentNumber
        );

      if (documentNumberIsAccountPrimaryDocument) {
        setIsUsingAccountDocument(true);

        return;
      }

      if (validateDocumentNumber(documentNumber)) {
        setErrorMessage(undefined);

        const accountTypeVerify =
          (ValidateCPF(debouncedSearchTerm) && AccountType.PHYSICAL) ||
          (ValidateCNPJ(debouncedSearchTerm) && AccountType.LEGAL) ||
          undefined;

        if (accountTypeVerify) {
          setAssignorAccountType(accountTypeVerify);
        }

        const accountPrimaryDocument = contract?.account?.documents?.find(
          doc =>
            doc.documentType === AccountDocumentType.CPF ||
            doc.documentType === AccountDocumentType.CNPJ
        );

        if (
          documentNumber !== values?.documentNumber &&
          documentNumber !== accountPrimaryDocument.documentNumber
        ) {
          setIsUsingAccountDocument(false);
          setFormInitialValues(undefined);
          setIsDrawerOpen(true);
        }
      } else {
        setErrorMessage("Número de documento inválido");
      }
    }
  }, [
    contract?.account?.documents,
    debouncedSearchTerm,
    isUsingAccountDocument,
    values?.documentNumber
  ]);

  const handleEditTermDataDraft = (termDataToEdit: ITermDataDraft) => {
    setFormInitialValues({
      fullName: termDataToEdit.fullName,
      documentNumber: termDataToEdit.documentNumber,
      email: termDataToEdit.email,
      whatsapp: termDataToEdit.whatsapp,
      zipCode: termDataToEdit.zipCode,
      street: termDataToEdit.street,
      number: termDataToEdit.number,
      neighborhood: termDataToEdit.neighborhood,
      city: termDataToEdit.city,
      uf: termDataToEdit.uf,
      note: termDataToEdit.note,
      legalRepresentativeName: termDataToEdit.legalRepresentativeName,
      legalRepresentativeDocument: termDataToEdit.legalRepresentativeDocument
    });
    setAssignorAccountType(termDataToEdit.accountType);
    setSearchTerm(termDataToEdit.documentNumber);
    setIsDrawerOpen(true);
  };
  const formFields = ownershipTransferFields({
    values: formInitialValues,
    accountType: assignorAccountType,
    documentNumber: debouncedSearchTerm
  });

  const handleSubmit = (
    values: SubmitOwnershipTransferFieldsValues,
    { setSubmitting }
  ) => {
    submitTermDataDraft({
      contract,
      termDataDraft,
      execute,
      setIsSubmitting: setSubmitting,
      setCanAdvance,
      isCustomForm: true,
      values,
      assignorAccountType,
      setIsDrawerOpen,
      setErrorMessage
    });
  };

  return termDataDraft.isLoadingList || isSubmitting ? (
    <OwnershipTransferShimmer />
  ) : (
    <Container>
      <Card
        width="60%"
        padding="2rem"
        borderRadius="0.75rem"
        margin="0 0 2rem 0"
      >
        <Stack
          style={{
            display: "flex",
            flexWrap: "wrap"
          }}
        >
          <h2 className="ms-fontSize-20" style={{ marginBottom: "0" }}>
            Documentos complementares
          </h2>

          <h3 className="ms-fontSize-16">
            Termos de Transferência de Titularidade (Documento 1 de 1)
          </h3>

          <p style={{ marginTop: "0.5rem", maxWidth: "40rem" }}>
            Para trazer a linha para a Coopercompany, precisamos que os dados do
            titular sejam idênticos aos do cadastro na operadora de origem.{" "}
            <br />
            Isso garante que a transferência seja aprovada sem complicações.
          </p>

          <div>
            <TextField
              id="input-search-assignor"
              label="CPF/CNPJ:"
              placeholder="Digite o CPF ou CNPJ do cedente"
              maxLength={18}
              onChange={(_, item) => {
                setSearchTerm(item);
              }}
              value={cpfCnpjFormatter(searchTerm || "")}
              errorMessage={!isDrawerOpen && errorMessage}
            />

            <Link
              className="ms-fontSize-12"
              style={{ userSelect: "none" }}
              disabled={isUsingAccountDocument}
              onClick={() => {
                submitTermDataDraft({
                  contract,
                  termDataDraft,
                  execute,
                  setCanAdvance,
                  isCustomForm: false,
                  assignorAccountType,
                  setErrorMessage,
                  setIsSubmitting
                });

                const accountPrimaryDocument =
                  contract?.account?.documents?.find(
                    doc =>
                      doc.documentType === AccountDocumentType.CPF ||
                      doc.documentType === AccountDocumentType.CNPJ
                  );

                setSearchTerm(accountPrimaryDocument?.documentNumber);
                setIsUsingAccountDocument(true);
              }}
            >
              {useMemberDataText}
            </Link>
          </div>

          {termDataDraft?.list[0]?.id && (
            <DisplayTermDataDraft
              termDataDraft={termDataDraft.list[0]}
              editAction={handleEditTermDataDraft}
            />
          )}

          <EditComplementaryDocuments
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            fields={formFields}
            validationSchema={ownershipTransferFieldsSchema(
              assignorAccountType
            )}
            onSubmit={handleSubmit}
            errorMessage={errorMessage}
          />
        </Stack>
      </Card>

      <PrimaryButton
        style={{ borderRadius: "0.4rem" }}
        text="Avançar"
        onClick={handleNextStep}
        disabled={!canAdvance}
      />
    </Container>
  );
}
